import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { ExternalLink, AlertTriangle } from 'lucide-react';
import { invoicesAPI, serviceCallsAPI, quickbooksAPI } from '@shared/apiClient';
import { getErrorMessage, getServiceCallListCustomerName, formatDate } from '@shared/utils';
import CopyTextButton from '../../components/CopyTextButton';
import CspireInvoiceEditor from '../../components/CspireInvoiceEditor';
import {
  collectServiceCallParts,
  formatCopyText,
  formatDisplayText,
  formatPartsList,
  formatQuickBooksHandoff,
  normalizePartsUsed,
} from './formatters';

const renderPartsUsed = (parts) => {
  const normalized = normalizePartsUsed(parts);
  if (normalized.length === 0) return '—';
  return (
    <div className="space-y-1">
      {normalized.map((part, index) => {
        const itemNumber = part.itemNumber || part.name || 'N/A';
        const description = part.description || part.name || '';
        const quantity = parseFloat(part.quantity) || 0;
        const cost = parseFloat(part.cost) || 0;
        const total = quantity * cost;
        return (
          <div key={index} className="text-sm text-gray-800">
            <span className="font-medium">{itemNumber}</span>
            {description ? ` — ${description}` : ''}
            {' · '}
            Qty {quantity.toFixed(2)} · ${cost.toFixed(2)} · ${total.toFixed(2)}
          </div>
        );
      })}
    </div>
  );
};

export default function InvoiceDetailPane({ call, mailbox, onCspirePrinted }) {
  const queryClient = useQueryClient();
  const [invoiceNumber, setInvoiceNumber] = useState(call?.invoiceNumber || '');
  const [suggestingNumber, setSuggestingNumber] = useState(false);
  const invoiceInputRef = useRef(null);

  useEffect(() => {
    setInvoiceNumber(call?.invoiceNumber || '');
  }, [call?.id, call?.invoiceNumber]);

  const saveInvoiceMutation = useMutation(
    (value) => serviceCallsAPI.updateServiceCall(call.id, { invoiceNumber: value || null }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['invoiceFolderItems']);
        queryClient.invalidateQueries(['serviceCall', call?.id]);
        toast.success('Invoice number saved');
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  const handleSaveInvoice = () => {
    if (!call) return;
    saveInvoiceMutation.mutate(invoiceNumber.trim());
  };

  const handleSuggestNextNumber = async () => {
    if (!call) return;
    setSuggestingNumber(true);
    try {
      const res = await invoicesAPI.getNextNumber();
      const next = res.data?.invoiceNumber || '';
      setInvoiceNumber(next);
      saveInvoiceMutation.mutate(next);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSuggestingNumber(false);
    }
  };

  const { data: qboStatusData } = useQuery('quickbooksStatus', () => quickbooksAPI.getStatus(), {
    staleTime: 60000,
    enabled: mailbox === 'customers',
  });
  const qboConnected = mailbox === 'customers' && qboStatusData?.data?.connected;
  const qboEnvironment = qboStatusData?.data?.environment;
  const qboInvoiceUrl = call?.qboInvoiceId && qboEnvironment
    ? `${qboEnvironment === 'production' ? 'https://app.qbo.intuit.com' : 'https://app.sandbox.qbo.intuit.com'}/app/invoice?txnId=${call.qboInvoiceId}`
    : null;

  const pushToQuickbooksMutation = useMutation(
    () => quickbooksAPI.pushServiceCall(call.id),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['invoiceFolderItems']);
        queryClient.invalidateQueries(['serviceCall', call?.id]);
        toast.success('Pushed to QuickBooks');
        if (res.data?.qboInvoiceUrl) {
          window.open(res.data.qboInvoiceUrl, '_blank', 'noopener,noreferrer');
        }
      },
      onError: (error) => toast.error(getErrorMessage(error)),
    }
  );

  if (mailbox === 'cspire') {
    return (
      <div className="flex-1 overflow-auto p-4 min-h-0">
        <CspireInvoiceEditor
          serviceCall={call}
          inline
          onPrinted={onCspirePrinted}
        />
      </div>
    );
  }

  const allParts = collectServiceCallParts(call);
  const workPerformedText = formatDisplayText(call?.workPerformed);
  const workPerformedCopyText = formatCopyText(call?.workPerformed);
  const partsCopyText = formatPartsList(allParts);
  const customerName = getServiceCallListCustomerName(call);
  const qbHandoffText = formatQuickBooksHandoff(call, customerName);

  return (
    <div className="flex-1 overflow-auto min-h-0">
      <div className="flex-shrink-0 px-4 py-3 border-b border-surface-border bg-surface">
        <h2 className="text-base font-semibold text-gray-900 truncate">{customerName}</h2>
        <p className="text-xs text-gray-500">
          Completed {call.completedDate ? formatDate(call.completedDate) : '—'}
          {call.technician && ` · ${call.technician.firstName} ${call.technician.lastName}`}
        </p>
      </div>

      <div className="p-4 space-y-4 max-w-4xl">
        <div className="card">
          <div className="card-header flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-semibold">Invoice #</span>
            <div className="flex items-center gap-2">
              {!invoiceNumber && (
                <span className="text-2xs text-amber-600 font-medium">Required from paper stack</span>
              )}
              <CopyTextButton text={qbHandoffText} label="Copy for QuickBooks" size="xs" />
            </div>
          </div>
          <div className="card-body flex gap-2 flex-wrap">
            <input
              ref={invoiceInputRef}
              type="text"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              onBlur={handleSaveInvoice}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveInvoice();
                }
              }}
              placeholder="Enter invoice number..."
              className="input text-base font-semibold max-w-xs"
            />
            <button type="button" className="btn-secondary btn-sm" onClick={handleSaveInvoice}>
              Save
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={handleSuggestNextNumber}
              disabled={suggestingNumber || saveInvoiceMutation.isLoading}
              title="Suggest the next unused numeric invoice number"
            >
              {suggestingNumber ? 'Suggesting…' : 'Suggest next #'}
            </button>
          </div>
          {qboConnected && (
            <div className="card-body pt-0 flex items-center gap-2 flex-wrap border-t border-surface-border mt-2 pt-3">
              {call.qboInvoiceId ? (
                <a
                  href={qboInvoiceUrl || '#'}
                  onClick={(e) => {
                    if (!qboInvoiceUrl) e.preventDefault();
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 rounded px-2 py-1 hover:bg-green-100"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Synced to QuickBooks
                </a>
              ) : (
                <button
                  type="button"
                  className="btn-primary btn-sm"
                  onClick={() => pushToQuickbooksMutation.mutate()}
                  disabled={pushToQuickbooksMutation.isLoading}
                >
                  {pushToQuickbooksMutation.isLoading ? 'Sending…' : 'Send to QuickBooks'}
                </button>
              )}
              {call.qboSyncError && !call.qboInvoiceId && (
                <span className="inline-flex items-center gap-1 text-2xs text-amber-700 bg-amber-50 rounded px-2 py-1">
                  <AlertTriangle className="h-3 w-3" />
                  {call.qboSyncError}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header flex items-center justify-between">
            <span className="text-sm font-semibold">Work Performed</span>
            <CopyTextButton text={workPerformedCopyText} />
          </div>
          <div className="card-body">
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{workPerformedText}</p>
          </div>
        </div>

        {(call.visits || []).map((visit) => {
          const visitWork = formatDisplayText(visit.workPerformed);
          const visitWorkCopy = formatCopyText(visit.workPerformed);
          const visitParts = normalizePartsUsed(visit.partsUsed);
          return (
            <div key={visit.id} className="card">
              <div className="card-header flex items-center justify-between">
                <span className="text-sm font-semibold">Visit #{visit.visitNumber}</span>
                <CopyTextButton text={visitWorkCopy} size="xs" />
              </div>
              <div className="card-body space-y-2">
                <p className="text-sm whitespace-pre-wrap">{visitWork}</p>
                {visitParts.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-gray-600">Visit parts</span>
                      <CopyTextButton text={formatPartsList(visitParts)} size="xs" />
                    </div>
                    {renderPartsUsed(visitParts)}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <div className="grid grid-cols-2 gap-3">
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <span className="text-sm font-semibold">Parts</span>
              <CopyTextButton text={partsCopyText} size="xs" />
            </div>
            <div className="card-body">{renderPartsUsed(allParts)}</div>
          </div>
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <span className="text-sm font-semibold">Total Cost</span>
              <CopyTextButton text={call.totalCost != null ? String(call.totalCost) : ''} size="xs" />
            </div>
            <div className="card-body text-sm font-semibold">
              {call.totalCost != null ? `$${Number(call.totalCost).toFixed(2)}` : '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
